import React, { useState, useEffect, useCallback } from 'react';
import { Users, Search, User, Package, TrendingUp, Calendar, ChevronLeft, ChevronRight, RefreshCw, Copy, CheckCircle, ChevronDown, ChevronUp, Network, Mail } from 'lucide-react';
import Layout from '../components/Layout';
import API_BASE_URL from '../config/api';

const MyDirect = () => {
    const [directReferrals, setDirectReferrals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [myReferralCode, setMyReferralCode] = useState('');
    const [copied, setCopied] = useState(false);

    // Expanded row states
    const [expandedRows, setExpandedRows] = useState({}); // { referrerHId: boolean }
    const [rowDownlines, setRowDownlines] = useState({}); // { referrerHId: [] }
    const [rowLoading, setRowLoading] = useState({}); // { referrerHId: boolean }

    const itemsPerPage = 10;

    const fetchDirectReferrals = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/user/my_direct.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();

            if (data.success) {
                setDirectReferrals(data.data || []);
                setMyReferralCode(data.my_referral_code || '');
            }
        } catch (error) {
            console.error('Failed to fetch direct referrals:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    const toggleRow = useCallback(async (hId) => {
        if (expandedRows[hId]) {
            setExpandedRows(prev => ({ ...prev, [hId]: false }));
            return;
        }

        setExpandedRows(prev => ({ ...prev, [hId]: true }));

        // Fetch downline if not already fetched
        if (!rowDownlines[hId]) {
            try {
                setRowLoading(prev => ({ ...prev, [hId]: true }));
                const token = localStorage.getItem('authToken');
                const response = await fetch(`${API_BASE_URL}/user/downline_list.php?user_id=${hId}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const data = await response.json();
                if (data.success) {
                    setRowDownlines(prev => ({ ...prev, [hId]: data.data || [] }));
                }
            } catch (err) {
                console.error('Failed to fetch downline list:', err);
            } finally {
                setRowLoading(prev => ({ ...prev, [hId]: false }));
            }
        }
    }, [expandedRows, rowDownlines]);

    useEffect(() => {
        fetchDirectReferrals();
    }, [fetchDirectReferrals]);

    const copyReferralCode = () => {
        navigator.clipboard.writeText(myReferralCode);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    // Filter and pagination
    const filteredReferrals = directReferrals.filter(ref =>
        ref.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ref.user_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ref.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const totalPages = Math.ceil(filteredReferrals.length / itemsPerPage);
    const paginatedReferrals = filteredReferrals.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        return new Date(dateStr).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    return (
        <Layout>
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-4 sm:p-6 lg:p-8">
                {/* Header */}
                <div className="mb-6 sm:mb-8">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div>
                            <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                                My Direct Referrals
                            </h1>
                            <p className="text-gray-500 mt-1">Users you have sponsored personally</p>
                        </div>
                        <button
                            onClick={fetchDirectReferrals}
                            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-all shadow-sm"
                        >
                            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                            <span>Refresh</span>
                        </button>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    <div className="bg-white rounded-2xl p-5 shadow-lg border border-gray-100">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-200">
                                <Users size={22} className="text-white" />
                            </div>
                            <div>
                                <p className="text-xs text-gray-500 uppercase tracking-wide">Total Direct</p>
                                <p className="text-2xl font-bold text-gray-900">{directReferrals.length}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl p-5 shadow-lg border border-gray-100">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg shadow-green-200">
                                <TrendingUp size={22} className="text-white" />
                            </div>
                            <div>
                                <p className="text-xs text-gray-500 uppercase tracking-wide">Total Team</p>
                                <p className="text-2xl font-bold text-gray-900">
                                    {directReferrals.reduce((sum, ref) => sum + (ref.team_size || 0), 0)}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl p-5 shadow-lg border border-gray-100">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-purple-200">
                                <Package size={22} className="text-white" />
                            </div>
                            <div>
                                <p className="text-xs text-gray-500 uppercase tracking-wide">Total PV</p>
                                <p className="text-2xl font-bold text-gray-900">
                                    {directReferrals.reduce((sum, ref) => sum + (ref.pv_points || 0), 0).toFixed(0)}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl p-5 shadow-lg relative overflow-hidden">
                        <div className="absolute inset-0 bg-white/10 backdrop-blur-sm"></div>
                        <div className="relative">
                            <p className="text-xs text-blue-100 uppercase tracking-wide mb-1">Your User ID</p>
                            <div className="flex items-center gap-2">
                                <p className="text-xl font-bold text-white tracking-wider">{myReferralCode || '---'}</p>
                                <button
                                    onClick={copyReferralCode}
                                    className="p-1.5 bg-white/20 rounded-lg hover:bg-white/30 transition-all"
                                >
                                    {copied ? <CheckCircle size={16} className="text-green-300" /> : <Copy size={16} className="text-white" />}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Search and Table */}
                <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                    {/* Search Bar */}
                    <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
                        <div className="relative max-w-md">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                            <input
                                type="text"
                                placeholder="Search by name, ID, or email..."
                                value={searchTerm}
                                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                                className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                            />
                        </div>
                    </div>

                    {/* Table */}
                    {loading ? (
                        <div className="p-16 flex flex-col items-center justify-center">
                            <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                            <p className="mt-4 text-gray-500">Loading referrals...</p>
                        </div>
                    ) : paginatedReferrals.length === 0 ? (
                        <div className="p-16 flex flex-col items-center justify-center">
                            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                                <Users size={32} className="text-gray-400" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-700">No Direct Referrals Yet</h3>
                            <p className="text-gray-500 text-sm mt-1">Share your referral code to grow your network!</p>
                        </div>
                    ) : (
                        <>
                            <div className="hidden lg:block overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="bg-gradient-to-r from-gray-50 to-gray-100">
                                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">Member</th>
                                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">User ID</th>
                                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">Position</th>
                                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">Package</th>
                                            <th className="px-6 py-4 text-center text-xs font-bold text-gray-600 uppercase tracking-wider">PV</th>
                                            <th className="px-6 py-4 text-center text-xs font-bold text-gray-600 uppercase tracking-wider">Team Size</th>
                                            <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">Joined</th>
                                            <th className="px-6 py-4 text-center text-xs font-bold text-gray-600 uppercase tracking-wider">Downline</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {paginatedReferrals.map((referral, index) => (
                                            <React.Fragment key={referral.id}>
                                                <tr className="hover:bg-blue-50/50 transition-colors">
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center text-white font-bold shadow-md">
                                                                {referral.name?.charAt(0) || <User size={16} />}
                                                            </div>
                                                            <div>
                                                                <p className="font-semibold text-gray-900">{referral.name}</p>
                                                                <p className="text-xs text-gray-500">{referral.email}</p>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">{referral.user_id}</span>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${referral.position?.toLowerCase() === 'left' || referral.position === 'L'
                                                            ? 'bg-blue-100 text-blue-700'
                                                            : 'bg-purple-100 text-purple-700'
                                                            }`}>
                                                            {referral.position?.toLowerCase() === 'left' || referral.position === 'L' ? 'LEFT' : 'RIGHT'}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <span className="px-3 py-1 bg-green-100 text-green-700 rounded-lg text-sm font-medium">
                                                            {referral.package || 'None'}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        <span className="font-bold text-gray-900">{referral.pv_points || 0}</span>
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        <div className="flex items-center justify-center gap-2">
                                                            <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded">L: {referral.left_count || 0}</span>
                                                            <span className="text-xs text-purple-600 bg-purple-50 px-2 py-0.5 rounded">R: {referral.right_count || 0}</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center gap-2 text-gray-600">
                                                            <Calendar size={14} />
                                                            <span className="text-sm">{formatDate(referral.joined_at)}</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        <button
                                                            onClick={() => toggleRow(referral.id)}
                                                            className={`p-2 rounded-lg transition-all ${expandedRows[referral.id] ? 'bg-indigo-600 text-white shadow-md' : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100'}`}
                                                            title="View Downline List"
                                                        >
                                                            {expandedRows[referral.id] ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                                                        </button>
                                                    </td>
                                                </tr>

                                                {/* Expanded Downline Row */}
                                                {expandedRows[referral.id] && (
                                                    <tr className="bg-slate-50/80 border-l-4 border-indigo-500 animate-in slide-in-from-top-2 duration-300">
                                                        <td colSpan="8" className="px-8 py-6">
                                                            <div className="bg-white rounded-2xl shadow-inner border border-slate-200 overflow-hidden">
                                                                <div className="bg-slate-50 px-6 py-3 border-b border-slate-200 flex items-center justify-between">
                                                                    <div className="flex items-center gap-2 text-indigo-700 font-bold text-sm">
                                                                        <Network size={16} />
                                                                        <span>Downline of {referral.name}</span>
                                                                    </div>
                                                                    <span className="text-xs font-bold text-slate-500 bg-slate-200/50 px-3 py-1 rounded-full">
                                                                        {rowDownlines[referral.id]?.length || 0} Members Found
                                                                    </span>
                                                                </div>

                                                                <div className="max-h-96 overflow-y-auto">
                                                                    {rowLoading[referral.id] ? (
                                                                        <div className="p-12 flex flex-col items-center justify-center">
                                                                            <div className="w-8 h-8 border-3 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                                                                            <p className="mt-2 text-xs text-slate-500 font-medium tracking-wide">Fetching downline users...</p>
                                                                        </div>
                                                                    ) : rowDownlines[referral.id]?.length === 0 ? (
                                                                        <div className="p-12 text-center">
                                                                            <Users size={32} className="mx-auto text-slate-300 mb-2" />
                                                                            <p className="text-slate-500 text-sm font-medium">No downline members found for this user.</p>
                                                                        </div>
                                                                    ) : (
                                                                        <table className="w-full text-xs">
                                                                            <thead className="sticky top-0 bg-slate-50 text-slate-500 font-bold uppercase tracking-wider text-[10px] border-b border-slate-100">
                                                                                <tr>
                                                                                    <th className="px-6 py-3 text-left">Level</th>
                                                                                    <th className="px-6 py-3 text-left">Name</th>
                                                                                    <th className="px-6 py-3 text-left">User ID</th>
                                                                                    <th className="px-6 py-3 text-left">Email</th>
                                                                                    <th className="px-6 py-3 text-left">Position</th>
                                                                                    <th className="px-6 py-3 text-left">Package</th>
                                                                                    <th className="px-6 py-3 text-right">PV</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody className="divide-y divide-slate-100">
                                                                                {rowDownlines[referral.id].map((dl, dlIdx) => (
                                                                                    <tr key={dl.id} className="hover:bg-indigo-50/30 transition-colors">
                                                                                        <td className="px-6 py-3">
                                                                                            <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded font-bold">Lvl {dl.level}</span>
                                                                                        </td>
                                                                                        <td className="px-6 py-3 font-bold text-slate-800">{dl.name}</td>
                                                                                        <td className="px-6 py-3 text-slate-500 font-mono">{dl.user_id}</td>
                                                                                        <td className="px-6 py-3 text-slate-400">
                                                                                            <div className="flex items-center gap-1.5">
                                                                                                <Mail size={12} />
                                                                                                {dl.email}
                                                                                            </div>
                                                                                        </td>
                                                                                        <td className="px-6 py-3">
                                                                                            <span className={`inline-flex items-center px-2 py-0.5 rounded font-black tracking-tighter ${dl.position === 'left' ? 'text-blue-600 bg-blue-50' : 'text-purple-600 bg-purple-50'}`}>
                                                                                                {dl.position?.toUpperCase()}
                                                                                            </span>
                                                                                        </td>
                                                                                        <td className="px-6 py-3">
                                                                                            <span className="text-emerald-600 font-bold">{dl.package}</span>
                                                                                        </td>
                                                                                        <td className="px-6 py-3 text-right font-black text-slate-700">{dl.pv}</td>
                                                                                    </tr>
                                                                                ))}
                                                                            </tbody>
                                                                        </table>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile Card View */}
                            <div className="lg:hidden divide-y divide-gray-100 p-2">
                                {paginatedReferrals.map((referral) => (
                                    <div key={referral.id} className="p-4 bg-white rounded-xl mb-3 border border-gray-100 shadow-sm flex flex-col gap-3">
                                        <div className="flex justify-between items-start">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center text-white font-bold shadow-md">
                                                    {referral.name?.charAt(0) || <User size={16} />}
                                                </div>
                                                <div>
                                                    <p className="font-bold text-gray-900">{referral.name}</p>
                                                    <p className="text-[10px] text-gray-500 font-mono">{referral.user_id}</p>
                                                </div>
                                            </div>
                                            <span className={`px-2 py-0.5 rounded-lg text-[10px] font-black tracking-wider ${referral.position?.toLowerCase() === 'left' || referral.position === 'L' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                                                {referral.position?.toLowerCase() === 'left' || referral.position === 'L' ? 'LEFT' : 'RIGHT'}
                                            </span>
                                        </div>

                                        <div className="grid grid-cols-2 gap-2 text-xs">
                                            <div className="bg-gray-50 p-2 rounded-lg border border-gray-100">
                                                <span className="text-gray-400 font-bold block mb-1">Package</span>
                                                <span className="text-green-700 font-bold">{referral.package || 'None'}</span>
                                            </div>
                                            <div className="bg-gray-50 p-2 rounded-lg border border-gray-100">
                                                <span className="text-gray-400 font-bold block mb-1">PV Points</span>
                                                <span className="text-gray-900 font-bold">{referral.pv_points || 0}</span>
                                            </div>
                                            <div className="bg-gray-50 p-2 rounded-lg border border-gray-100 col-span-2 flex justify-between items-center">
                                                <span className="text-gray-400 font-bold">Team Size (L/R)</span>
                                                <div className="flex gap-2">
                                                    <span className="text-blue-600 font-bold">L: {referral.left_count || 0}</span>
                                                    <span className="text-purple-600 font-bold">R: {referral.right_count || 0}</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex justify-between items-center border-t border-gray-50 pt-3">
                                            <div className="flex items-center gap-1.5 text-gray-500 text-xs font-bold">
                                                <Calendar size={12} />
                                                <span>{formatDate(referral.joined_at)}</span>
                                            </div>
                                            <button
                                                onClick={() => toggleRow(referral.id)}
                                                className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${expandedRows[referral.id] ? 'bg-indigo-600 text-white shadow-md' : 'bg-indigo-50 text-indigo-600 border border-indigo-100'}`}
                                            >
                                                Downline
                                                {expandedRows[referral.id] ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                            </button>
                                        </div>

                                        {expandedRows[referral.id] && (
                                            <div className="mt-2 text-xs border border-indigo-100 rounded-lg overflow-hidden animate-in slide-in-from-top-2">
                                                <div className="bg-indigo-50 px-3 py-2 text-indigo-700 font-bold border-b border-indigo-100 flex justify-between items-center">
                                                    <span>Downline</span>
                                                    <span className="bg-white px-2 py-0.5 rounded-full shadow-sm">{rowDownlines[referral.id]?.length || 0}</span>
                                                </div>
                                                <div className="max-h-64 overflow-y-auto bg-white p-2">
                                                    {rowLoading[referral.id] ? (
                                                        <div className="p-4 text-center text-indigo-400 font-bold">Loading...</div>
                                                    ) : rowDownlines[referral.id]?.length === 0 ? (
                                                        <div className="p-4 text-center text-gray-400 font-bold">No downline members</div>
                                                    ) : (
                                                        <div className="flex flex-col gap-2">
                                                            {rowDownlines[referral.id].map(dl => (
                                                                <div key={dl.id} className="p-2 border border-gray-100 rounded bg-gray-50">
                                                                    <div className="flex justify-between items-center mb-1">
                                                                        <span className="font-bold text-gray-800">{dl.name}</span>
                                                                        <span className="font-bold text-gray-500 text-[10px]">Lvl {dl.level}</span>
                                                                    </div>
                                                                    <div className="flex justify-between items-center text-[10px]">
                                                                        <span className="text-gray-500">{dl.user_id}</span>
                                                                        <span className={dl.position === 'left' ? 'text-blue-600' : 'text-purple-600'}>
                                                                            {dl.position?.toUpperCase()}
                                                                        </span>
                                                                    </div>
                                                                    <div className="flex justify-between items-center text-[10px] mt-1 pt-1 border-t border-gray-200">
                                                                        <span className="text-emerald-600 font-bold">{dl.package}</span>
                                                                        <span className="text-gray-700 font-black">{dl.pv} PV</span>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="p-4 border-t border-gray-100 flex items-center justify-between">
                                    <p className="text-sm text-gray-500">
                                        Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredReferrals.length)} of {filteredReferrals.length}
                                    </p>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                            disabled={currentPage === 1}
                                            className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <ChevronLeft size={18} />
                                        </button>
                                        <span className="px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold">
                                            {currentPage}
                                        </span>
                                        <button
                                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                            disabled={currentPage === totalPages}
                                            className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <ChevronRight size={18} />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default MyDirect;
